import React, { useEffect, useState } from 'react';
import { Paper, Box, Button, Tooltip } from '@mui/material';
import { useDispatch } from 'react-redux';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';
import { ChartTypeSelector } from './ChartTypeSelector';
import { ChartFactory } from '../Charts/ChartFactory';
import { ChartTitle } from './ChartTitle';
import { useReport } from '../ReportContext';
import { useFilters } from 'components/Elements/Filters/FilterContext';
import { DrilldownNavigation } from './DrilldownNavigation';
import { NoDataMessage } from './NoDataMessage';
import { InvalidFiltersMessage } from './InvalidFiltersMessage';
import { ViewDataButton } from './ViewDataButton';
import { ExportDropdown } from './ExportDropdown';
import { useChartExport } from '../hooks';
import { showError } from '../../../../redux/common/actions';
import CMAlertConfigDialog from '../Components/CMAlertConfigDialog';

export const ChartContainer = ({
  title,
  subtitle,
  data,
  isLoading,
  isDrilledDown,
  invalidFiltersMessage,
  LoadingComponent
}) => {
  const { config, selectedChartType, setSelectedChartType } = useReport();
  const { buildUrlParams } = useFilters();
  const dispatch = useDispatch();

  const [showTimestamp, setShowTimestamp] = useState(false);
  const [timestampText, setTimestampText] = useState('');
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);

  const allowCMAlertConfig = config.cmAlertConfig?.enabled;
  const selectedQuestionId = allowCMAlertConfig
    ? buildUrlParams().modelVersionTaskQuestionId
    : undefined;

  const { triggerExport, containerRef: exportContainerRef, isExporting } = useChartExport({
    title,
    backgroundColor: '#ffffff',
  });

  useEffect(() => {
    if (!selectedChartType && config.defaultChartType) {
      setSelectedChartType(config.defaultChartType);
    }
  }, [config.defaultChartType, selectedChartType, setSelectedChartType]);

  const hasData = data && (
    (Array.isArray(data) && data.length > 0) ||
    (typeof data === 'object' && !Array.isArray(data) && 
     Object.values(data).length > 0 && 
     Object.values(data).every(d => Array.isArray(d) && d.length > 0))
  );

  const handlePngExport = async () => {
    const now = new Date();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timestamp = `Exported on ${now.toLocaleString()} (${timeZone})`;
    setTimestampText(timestamp);
    setShowTimestamp(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 50));
      await triggerExport();
    } catch (error) {
      console.error("Export process failed in ChartContainer");
    } finally {
      setShowTimestamp(false);
      setTimestampText('');
    }
  };

  return (
    <>
    <Paper

      elevation={0}
      ref={exportContainerRef}
      sx={{ 
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '8px',
        padding: '24px 32px 32px 32px',
        position: 'relative',
        marginTop: '16px',
        overflow: 'visible',
        backgroundColor: '#ffffff'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '32px',
        position: 'relative'
      }}>
        <ChartTitle
          title={title}
          subTitle={subtitle}
          isDrilledDown={isDrilledDown}
        />
        {!invalidFiltersMessage && (
          <Box sx={{ marginTop: '8px' }}>
            <DrilldownNavigation />
          </Box>
        )}
        {hasData && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              position: 'absolute',
              top: 0,
              right: 0
            }}
            data-html2canvas-ignore="true"
          >
            <ViewDataButton 
              data={data} 
              config={config} 
            />
            <ChartTypeSelector
              allowedTypes={config.allowedChartTypes}
              currentType={selectedChartType}
              onTypeChange={setSelectedChartType}
            />
            <ExportDropdown
              onPngExport={handlePngExport}
              isExporting={isExporting}
            />
            {allowCMAlertConfig && (
              <Tooltip
                title={
                  selectedQuestionId
                    ? 'Edit Alert'
                    : 'Select a question to edit its alert'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setAlertDialogOpen(true)}
                    disabled={!selectedQuestionId}
                    aria-label="Edit Rate of Change Alert"
                    sx={{
                      minWidth: '40px',
                      height: '32px',
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      color: 'rgb(48,122,215)',
                      '&:hover': {
                        backgroundColor: 'rgba(48,122,215,0.1)',
                        borderColor: 'rgb(48,122,215)',
                      },
                    }}
                  >
                    <EditNotificationsIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      <Box 
        sx={{ 
          '& .recharts-wrapper': {
            margin: '0 auto',
            maxWidth: '100%'
          },
          minHeight: '300px',
          padding: '0',
          position: 'relative',
          display: 'block',
          clear: 'both'
        }}
      >
        {isLoading ? (
          LoadingComponent
        ) : invalidFiltersMessage ? (
          <InvalidFiltersMessage message={invalidFiltersMessage} />
        ) : hasData ? (
          <div>
            <ChartFactory 
              type={selectedChartType}
              data={data}
              config={config}
              isDrilledDown={isDrilledDown}
            />
            {showTimestamp && (
              <div 
                style={{ 
                  textAlign: 'center', 
                  marginTop: '16px', 
                  fontSize: '0.75rem', 
                  color: '#888' 
                }}
              >
                {timestampText}
              </div>
            )}
          </div>
        ) : (
          <NoDataMessage />
        )}
      </Box>
    </Paper>
    {allowCMAlertConfig && (
      <CMAlertConfigDialog
        open={alertDialogOpen}
        closeHandler={() => setAlertDialogOpen(false)}
        modelVersionTaskQuestionId={selectedQuestionId}
        getError={(msg) => dispatch(showError(msg))}
      />
    )}
    </>
  );
};